import {
  Bot,
  BrainCircuit,
  Compass,
  Lightbulb,
} from "lucide-react";
import type {
  GuideSlide,
  GuideStep,
} from "@/components/training/SlideGuide";
import { CHIEF_OF_STAFF_OPERATING_BRIEF } from "@/data/chiefOfStaffOperatingBrief.js";

export { CHIEF_OF_STAFF_OPERATING_BRIEF } from "@/data/chiefOfStaffOperatingBrief.js";

export const FUTURE_OF_WORK_VIDEO_URL = "https://www.youtube.com/watch?v=TXiaDHn1dUk";

export const S2P_COUNCIL_STEPS: GuideStep[] = [
  {
    id: "future",
    title: "Build teammates, not more tools",
    description: "AI gets valuable when your team's expertise survives the chat.",
    duration: "3 min",
    whyItMatters:
      "Models keep getting smarter. Your advantage is the expertise your team can package, improve, and hand to the next person.",
    actions: [
      "Name one job where a reliable expert still beats a brilliant generalist.",
      "Choose one piece of that expertise worth packaging as a reusable Skill.",
    ],
    checkpoint:
      "You can name the first expertise you would package and the real problem it should solve.",
    icon: Compass,
    links: [
      {
        label: "Watch the 7-minute Future of Work video",
        href: FUTURE_OF_WORK_VIDEO_URL,
        external: true,
      },
    ],
  },
  {
    id: "leadership",
    title: "Pick the right gear",
    description: "Use the lightest AI gear that can finish the job.",
    duration: "5 min",
    whyItMatters:
      "More autonomy is not always better. Good leaders match the gear to the work, set the boundaries, and stay accountable for the result.",
    actions: [
      "Use Chat for a question, Assistant for a deliverable, Agents for a multi-step objective, and Loops for a recurring result.",
      "Avoid the three traps: tool thinking, one giant agent, and no improvement loop.",
    ],
    checkpoint:
      "You can choose the right gear and name the human judgement that still matters.",
    icon: BrainCircuit,
  },
  {
    id: "how-might-we",
    title: "Start with the finish line",
    description: "Name the work, the outcome, the boundary, and the sources.",
    duration: "3 min",
    whyItMatters:
      "A clear brief tells AI what good looks like before it starts.",
    actions: [
      "Write one sentence: How might we VERB OUTCOME without CONSTRAINTS based on SOURCES?",
    ],
    checkpoint:
      "Your sentence names one observable outcome, one meaningful boundary, and the sources the work should trust.",
    icon: Lightbulb,
  },
  {
    id: "chief-of-staff",
    title: "Build your first Chief of Staff",
    description: "Turn scattered context into one ranked, evidence-linked brief.",
    duration: "6 min",
    whyItMatters:
      "The win is not another summary. It is a clear decision about what deserves your attention next.",
    actions: [
      "Choose when it runs.",
      "Name the sources it may read.",
      "Teach your Do / Delegate / Delete rules.",
      "Require evidence, blockers, owners, and next steps.",
      "Start read-only. Review one run before expanding anything.",
    ],
    checkpoint:
      "You have one copyable brief for a first read-only run.",
    icon: Bot,
    prompts: [
      {
        label: "Chief of Staff starter brief",
        value: CHIEF_OF_STAFF_OPERATING_BRIEF,
      },
    ],
  },
];

export const S2P_COUNCIL_SLIDES: GuideSlide[] = [
  { id: "start", title: "AI Business Council · August", kind: "cover", tone: "paper" },
  { id: "four-gears", title: "Use the lightest gear that can finish the job", kind: "custom", tone: "paper", stepId: "future" },
  { id: "agent-skills-explained", title: "Skills turn team expertise into repeatable AI behavior", kind: "custom", tone: "paper", stepId: "future" },
  { id: "future-of-work", title: "Treating AI like a tool instead of a team.", kind: "custom", tone: "paper", stepId: "future" },
  { id: "ai-team-management-model", title: "AI teams improve the same way people do.", kind: "custom", tone: "paper", stepId: "future" },
  { id: "three-layers", title: "Understanding has three layers", kind: "custom", tone: "paper", stepId: "leadership" },
  { id: "connections-and-tools", title: "Set up Connections & Tools", kind: "custom", tone: "paper", stepId: "leadership" },
  { id: "atomic-reasoning-protocol", title: "Atomic Reasoning", kind: "custom", tone: "paper", stepId: "leadership" },
  { id: "how-might-we", title: "How might we VERB OUTCOME without CONSTRAINTS based on SOURCES", kind: "custom", tone: "paper", stepId: "how-might-we" },
  { id: "outcome-first-prompt-example", title: "“How might we rewrite this sales page to increase qualified calls without changing the offer, based on customer interviews, PostHog behavior, and our best-performing page?”", kind: "custom", tone: "paper", stepId: "how-might-we" },
  { id: "chief-of-staff", title: "Build Your Chief of Staff Agent", kind: "prompt", tone: "paper", stepId: "chief-of-staff", eyebrow: "Turn scattered context into clear priorities", actionIndexes: [0, 1, 2, 3, 4], promptIndexes: [0], compactLayout: true },
];
